import { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { KpiCard } from "../components/KpiCard";
import api from "../lib/api";
import { inr, fmtDate } from "../lib/format";
import {
  Building2, Megaphone, Wallet, AlertCircle, BadgeCheck, Activity,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { CHART_SERIES, CHART_GRID_COLOR, CHART_AXIS_COLOR, CHART_TOOLTIP_STYLE } from "../lib/chartTheme";

const PIE_COLORS = CHART_SERIES;

export function SuperAdminDashboard() {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/admin/dashboard", { skipGlobalLoader: true }).then((r) => setData(r.data));
  }, []);

  const k = data?.kpis;

  return (
    <Layout
      title="Platform Overview"
      subtitle="Hoardexa Super Admin · all advertiser companies"
      actions={
        <button
          onClick={() => nav("/super-admin/advertisers")}
          data-testid="goto-advertisers"
          className="hx-primary-action"
        >
          + Advertiser
        </button>
      }
    >
      {!data ? (
        <div className="space-y-6 animate-pulse">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[1,2,3,4].map((i) => <div key={i} className="h-32 rounded-xl bg-slate-200" />)}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[1,2,3,4].map((i) => <div key={i} className="h-32 rounded-xl bg-slate-200" />)}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="h-80 rounded-xl bg-slate-200 lg:col-span-2" />
            <div className="h-80 rounded-xl bg-slate-200" />
          </div>
        </div>
      ) : (
        <div className="space-y-6" data-testid="super-admin-dashboard">

          {/* Business KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <KpiCard testId="kpi-advertisers" label="Advertisers" value={k.total_advertisers} icon={Building2} accent="red" trend={`${k.active_subscriptions} active`} />
            <KpiCard testId="kpi-mrr" label="Monthly Revenue" value={inr(k.monthly_recurring_revenue)} icon={Wallet} accent="emerald" trend="Across active plans" />
            <KpiCard testId="kpi-hoardings" label="Hoardings" value={k.total_hoardings} icon={Megaphone} accent="blue" trend={`${k.occupied_hoardings} occupied`} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <KpiCard testId="kpi-renewals" label="Renewals 30d" value={k.pending_renewals} icon={AlertCircle} accent="amber" />
            <KpiCard testId="kpi-active" label="Active Subs" value={k.active_subscriptions} icon={BadgeCheck} accent="emerald" />
            <KpiCard testId="kpi-occupied" label="Occupied" value={k.occupied_hoardings} icon={Activity} accent="emerald" />
            <KpiCard testId="kpi-available" label="Available" value={k.total_hoardings - k.occupied_hoardings} icon={Megaphone} accent="red" />
          </div>

          {/* ── MRR Trend + Plan Distribution ──────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="hx-card p-6 lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-display font-semibold text-slate-900">MRR Trend</h3>
                  <p className="text-xs text-slate-500">Monthly recurring revenue across plans</p>
                </div>
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.mrr_series}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_COLOR} vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: CHART_AXIS_COLOR }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: CHART_AXIS_COLOR }} axisLine={false} tickLine={false} tickFormatter={(v) => inr(v)} />
                    <Tooltip formatter={(v) => inr(v)} contentStyle={CHART_TOOLTIP_STYLE} />
                    <Line type="monotone" dataKey="mrr" stroke="#1d4ed8" strokeWidth={2.5} dot={{ r: 4, fill: "#1d4ed8" }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="hx-card p-6">
              <div className="mb-4">
                <h3 className="font-display font-semibold text-slate-900">Plan Distribution</h3>
                <p className="text-xs text-slate-500">Active advertisers by plan</p>
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.plan_distribution} dataKey="count" nameKey="plan" innerRadius={50} outerRadius={85} paddingAngle={3}>
                      {data.plan_distribution.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* ── Recent Advertisers ────────────────────────────────── */}
          <div className="hx-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-slate-900">Recent Advertisers</h3>
              <button onClick={() => nav("/super-admin/advertisers")} className="text-xs font-semibold text-blue-600 hover:text-blue-700" data-testid="view-all-advertisers">View all →</button>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left py-3 px-2 font-semibold">Company</th>
                  <th className="text-left py-3 px-2 font-semibold">Plan</th>
                  <th className="text-left py-3 px-2 font-semibold">Status</th>
                  <th className="text-left py-3 px-2 font-semibold">Hoardings</th>
                  <th className="text-left py-3 px-2 font-semibold">Joined</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_advertisers.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-2 font-medium">{a.company_name}</td>
                    <td className="py-3 px-2"><span className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-100">{a.plan_type || "Standard"}</span></td>
                    <td className="py-3 px-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs border
                        ${a.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                        {a.status}
                      </span>
                    </td>
                    <td className="py-3 px-2 font-mono text-slate-700">{a.hoardings_used}</td>
                    <td className="py-3 px-2 text-slate-500">{fmtDate(a.created_at)}</td>
                  </tr>
                ))}
                {data.recent_advertisers.length === 0 && (
                  <tr><td colSpan={5} className="py-8 text-center text-slate-500">No advertisers yet.</td></tr>
                )}
              </tbody>
            </table>
            </div>
          </div>

        </div>
      )}
    </Layout>
  );
}
